import React, { useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import I18n from '@utils/i18n';
import dayjs from 'dayjs';
import { setSettings } from './Redux/settingsSlice';
import Scheduler from './Scheduler';
import { resetFilters } from './Redux/visitsSlice';
import GoogleMap from './GoogleMap';
import FilterPanel from '../../components/FilterPanel';
import { saveCacheToLocalStorage } from '../Dashboards/InvoiceInsights/helpers/localStorage';
import {
  removeError,
  addError,
  setVisitStatusOnAssign,
  setStatus,
  setInitialActiveDay,
  status as schedulerStatuses
} from './Redux/schedulerSlice';
import SettingsPopover from './SettingsPopover';
import { fetchVisitStatus } from './DAL/dataAccess';
import { setFleetInfo } from './Redux/fleetTrackingSlice';
import FleetTrackingModal from './FleetTrackingModal';
import { SchedulerContextProvider } from './Contexts/SchedulerContext.tsx';
import { RearrangeContextProvider } from './Contexts/RearrangeContext.tsx';
import { GlobalDragContext } from './Contexts/GlobalDragContext.tsx';
import { VisitPaneContextProvider } from './Contexts/VisitPaneContext.tsx';
import { ScheduleAndDispatchContext } from './Contexts/ScheduleAndDispatchContext.tsx';
import { TenantContext } from '../../contexts/tenant-context.tsx';
import { TimeZoneContext } from '@contexts/timezone-context';
import VisitsPaneNew from './VisitsPane/VisitsPane.tsx';
import VisitsPaneFullWidth from './VisitsPane/VisitsPaneFullWidth.tsx';
import useFilters from './Hooks/useFilters';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';

const DEFAULT_CACHE_KEY = 'map_scheduler';
const VISIT_STATUS_SCHEDULED = 'scheduled';

const namespace = 'features.map_scheduler.index';

function MapScheduler({ selectOptions, cacheKey, assetsForSelect, configuration, permissions, fleetTracking }) {
  const kendoCacheKey = `kendo/${cacheKey}`;
  const { inspectionTypesForSelect, workOrderStatusesForSelect, territoriesForSelect = [] } = selectOptions;

  const mapActive = useSelector((state) => state.map.mapActive);
  const visitsActive = useSelector((state) => state.map.visitsActive);
  const boardActive = useSelector((state) => state.scheduler.schedulerActive);
  const visitStatusScheduledActive = useSelector((state) => state.scheduler.visitStatusScheduledActive);
  const preserveDaySpanActive = useSelector((state) => state.scheduler.preserveDaySpanActive);
  const workspaceRef = useRef(null);
  const workspaceMapRef = useRef(null);
  const dispatch = useDispatch();
  const fleetTrackingModalTarget = useSelector((state) => state.fleet.selectedTech);
  const visitDateFilterMessage = useSelector((state) => state.map.visitDateFilterMessage);
  const filterValuesState = useSelector((state) => state.map.filters.values);
  const filterActiveState = useSelector((state) => state.map.filters.active);
  const errorList = useSelector((state) => state.scheduler.errorList);
  const showMapWorkspace = visitsActive || mapActive;

  const schedulerFilters = useSelector((state) => state.scheduler.filters);
  const allFilters = useSelector((state) => state.map.filters);
  const filtersRef = useRef();
  filtersRef.current = allFilters;

  const { tenant } = useContext(TenantContext);
  const { dragItem } = useContext(GlobalDragContext);
  const { activeModal, setVisitScheduledOnAssignment, setPreserveDaySpan } = useContext(ScheduleAndDispatchContext);
  const { timezone } = useContext(TimeZoneContext);

  useEffect(() => {
    dispatch(setSettings({ timezone }));
  }, []);

  useEffect(() => {
    dispatch(setInitialActiveDay({ timezone }));
  }, []);

  useEffect(() => {
    dispatch(setFleetInfo(fleetTracking));
  }, []);

  const handleResetCache = () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm(I18n.t('generic.are_you_sure'))) return;

    localStorage.removeItem(kendoCacheKey);
    window.location.reload();
  };

  const handleResizerMouseDown = () => {
    document.addEventListener('mousemove', handleResizerMouseMove);
    document.addEventListener('mouseup', handleResizerMouseUp, { once: true });
  };

  const handleResizerMouseMove = (e) => {
    if (!workspaceMapRef.current.style.flexBasis) {
      workspaceMapRef.current.style.flexBasis = `${workspaceMapRef.current.getBoundingClientRect().height}px`;
    } else {
      workspaceMapRef.current.style.flexBasis = `calc(${workspaceMapRef.current.style.flexBasis} + ${e.movementY}px)`;
    }
  };

  const handleResizerMouseUp = () => {
    document.removeEventListener('mousemove', handleResizerMouseMove);
    document.removeEventListener('mouseup', handleResizerMouseUp);
  };

  const mapPageFilters = useFilters({
    filterValuesState,
    filterActiveState,
    tenant,
    assetsForSelect,
    inspectionTypesForSelect,
    dispatch,
    namespace,
    visitDateFilterMessage
  });

  useEffect(() => {
    fetchVisitStatus(VISIT_STATUS_SCHEDULED)
      .then((result) => dispatch(setVisitStatusOnAssign(result)))
      .catch((error) => {
        dispatch(setStatus(schedulerStatuses.ERROR));
        dispatch(addError(error.message));
      });
  }, []);

  useEffect(() => {
    setVisitScheduledOnAssignment(visitStatusScheduledActive);
    setPreserveDaySpan(preserveDaySpanActive);

    saveCacheToLocalStorage(kendoCacheKey, {
      filters: filtersRef.current,
      boardActive,
      mapActive,
      visitsActive,
      visitStatusScheduledActive,
      preserveDaySpanActive
    });
  }, [allFilters, boardActive, mapActive, visitsActive, visitStatusScheduledActive, preserveDaySpanActive]);

  useEffect(() => {
    if (!workspaceMapRef.current) {
      return;
    }

    if (boardActive) {
      if (!workspaceMapRef.current.style.flexBasis) {
        workspaceMapRef.current.style.flexBasis = `${workspaceMapRef.current.getBoundingClientRect().height}px`;
      } else {
        workspaceMapRef.current.style.flexBasis = '50%';
      }
    } else {
      workspaceMapRef.current.style.flexBasis = '100%';
    }
  }, [boardActive]);

  const onResetFilters = () => {
    dispatch(resetFilters());
  };

  const visitsPaneFilters = useMemo(
    () => ({
      start: dayjs(filterValuesState.visitDate.startDate).isValid()
        ? dayjs(filterValuesState.visitDate.startDate).startOf('day').toDate()
        : undefined,
      end: dayjs(filterValuesState.visitDate.endDate).isValid()
        ? dayjs(filterValuesState.visitDate.endDate).endOf('day').toDate()
        : undefined,
      assigneeTechIds: filterValuesState.assigneeTechIds,
      assigneeSubIds: filterValuesState.assigneeSubIds,
      teamIds: filterValuesState.technicianTeam,
      accountIds: filterValuesState.account,
      assets: filterValuesState.asset,
      buildingIds: filterValuesState.building,
      territoryIds: filterValuesState.territory,
      tagIds: filterValuesState.tags,
      buildingTagIds: filterValuesState.buildingTags,
      accountTagIds: filterValuesState.accountTags,
      workOrderTagIds: filterValuesState.workOrderTags,
      zipCode: filterValuesState.zipCode,
      visitType: filterValuesState.type === '' ? undefined : filterValuesState.type,
      // workOrderStatuses: filterValuesState.workOrderStatus,
      visitStatuses: filterValuesState.visitStatusKeys,
      inspectionStatuses: filterValuesState.inspectionStatusKeys,
      inspectionTypeIds: filterValuesState.inspectionType
    }),
    [filterValuesState]
  );

  return (
    <div ref={workspaceRef} className="workspace__major--desktop-full">
      {dragItem}
      {activeModal}
      {fleetTrackingModalTarget && <FleetTrackingModal />}

      <FilterPanel onResetFilters={onResetFilters} onResetCache={handleResetCache} filters={mapPageFilters}>
        <SettingsPopover permissions={permissions} />
      </FilterPanel>
      <hr className="workspace__split" />

      <SchedulerContextProvider
        teamId={schedulerFilters.values.technicianTeam}
        territoryIds={
          // Same availability gate as Scheduler Manage Filters: never apply a
          // cached local territory selection when the control is hidden.
          !hasGlobalTerritoriesApplied() &&
          territoriesForSelect.length > 1 &&
          schedulerFilters.active?.territory &&
          Array.isArray(schedulerFilters.values.territory) &&
          schedulerFilters.values.territory.length > 0
            ? schedulerFilters.values.territory
            : undefined
        }>
        {showMapWorkspace && (
          <VisitPaneContextProvider filters={visitsPaneFilters}>
            <div ref={workspaceMapRef} className="workspace__map">
              {visitsActive && (mapActive ? <VisitsPaneNew /> : <VisitsPaneFullWidth />)}

              <GoogleMap visible={mapActive} centerLat={configuration.centerLat} centerLng={configuration.centerLng} />
            </div>
          </VisitPaneContextProvider>
        )}

        {boardActive && (
          <>
            {showMapWorkspace && (
              <>
                <div role="presentation" className="workspace__resizer--y-axis" onMouseDown={handleResizerMouseDown} />
                <hr className="workspace__split" />
              </>
            )}

            <RearrangeContextProvider>
              <Scheduler territoriesForSelect={selectOptions?.territoriesForSelect || []} />
            </RearrangeContextProvider>
          </>
        )}
      </SchedulerContextProvider>

      <NotificationGroup
        style={{
          right: 0,
          bottom: 0,
          alignItems: 'flex-start',
          flexWrap: 'wrap-reverse'
        }}>
        <Fade>
          {errorList.map((e, idx) => (
            <Notification
              key={idx}
              type={{ style: 'error', icon: true }}
              closable
              onClose={() => {
                dispatch(removeError(e.key));
              }}>
              <span>{e.message}</span>
            </Notification>
          ))}
        </Fade>
      </NotificationGroup>
    </div>
  );
}

MapScheduler.propTypes = {
  cacheKey: PropTypes.string,
  selectOptions: PropTypes.object,
  assetsForSelect: PropTypes.array,
  configuration: PropTypes.object,
  permissions: PropTypes.object,
  fleetTracking: PropTypes.object
};

MapScheduler.defaultProps = {
  cacheKey: DEFAULT_CACHE_KEY,
  selectOptions: {},
  assetsForSelect: [],
  configuration: {},
  permissions: {},
  fleetTracking: null
};

export default MapScheduler;
